import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { logOut } from "./authSlice";
import { apiURL } from "../../url/url";
import { ROUTES } from "../../routes/paths";

// Export the interface for the state
export interface AzureAuthState {
  isLoading: boolean;
  error: string | null;
  loginUrl: string | null;
  callbackProcessing: boolean;
  callbackError: string | null;
}

const initialState: AzureAuthState = {
  isLoading: false,
  error: null,
  loginUrl: null,
  callbackProcessing: false,
  callbackError: null,
};

export const getAzureLoginUrl = createAsyncThunk("azureAuth/getLoginUrl", async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get(apiURL + "/identity/azuread/get-login-url");
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue(error.response.data || "Failed to get login URL");
    }
    return rejectWithValue("An unknown error occurred");
  }
});

// Now accepts an optional email for login_hint
export const initiateAzureLogin = createAsyncThunk(
  "azureAuth/initiateLogin",
  async (email: string | undefined, { rejectWithValue }) => {
    try {
      const loginUrl = email
        ? `${apiURL}/identity/azuread/login?login_hint=${encodeURIComponent(email)}`
        : `${apiURL}/identity/azuread/login`;

      sessionStorage.setItem("loginType", "Azure");
      window.location.href = loginUrl;
    } catch (error) {
      console.error("Failed to initiate Azure login", error);
      return rejectWithValue("Failed to initiate login");
    }
  }
);

// export const processAuthCallback = createAsyncThunk(
//   "azureAuth/processCallback",
//   async (searchParams: string, { dispatch, rejectWithValue }) => {
//     try {
//       const urlParams = new URLSearchParams(searchParams);
//       const email = urlParams.get("email");

//       // Read token from cookie (not URL)
//       const token = getCookie("auth_token");
//       const refreshToken = getCookie("auth_refresh_token");
// 			// const sid = getCookie("auth_sid");
//       const loginHint = getCookie("auth_login_hint");

//       if (token && email) {
//         sessionStorage.setItem("loginType", "Azure");
//       	sessionStorage.setItem("login_hint", loginHint || email);
//         // if (sid) {
//         //   sessionStorage.setItem("auth_sid", sid);
//         // }

// 				 // Consume all auth cookies immediately
// 				deleteCookie("auth_token");
// 				deleteCookie("auth_refresh_token");
// 				//deleteCookie("auth_sid");
// 				//deleteCookie("auth_login_hint");
// 			  //deleteCookie("auth_email");

//         dispatch(setCredentials({
//           user: email,
//           accessToken: token,
//           refreshToken: refreshToken ?? null,
//         }));

//         return { success: true };
//       }

//       // Fallback — no token found anywhere
//       return rejectWithValue("Authentication token not found. Please try logging in again.");

//     } catch (error: unknown) {
//       console.error("Auth callback error:", error);
//       if (error instanceof Error) {
//         return rejectWithValue(error.message || "Authentication failed");
//       }
//       return rejectWithValue("Authentication failed");
//     }
//   }
// );

export const logoutUser = createAsyncThunk(
  "azureAuth/logout",
  async (_, { dispatch }) => {
    // Grab email before clearing state
    const email = sessionStorage.getItem("LoggedUserEmail") ?? "";
  	const loginHint = sessionStorage.getItem("login_hint") ?? email;
    //const sid = sessionStorage.getItem("auth_sid") ?? "";
    // Clear all local state first
    dispatch(logOut());

		// Clear backend session
    try {
      await fetch(`/api/identity/azuread/logout`, {
        method: 'GET',
        credentials: 'include'

      });
    } catch {
      // continue regardless
    }

		// Build logout URL with correct hint
    const tenantId = import.meta.env.VITE_AZURE_TENANT_ID;
    const postLogoutUri = import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI || `${window.location.origin}${ROUTES.login}`;

    const params = new URLSearchParams({
      post_logout_redirect_uri: postLogoutUri,
    });

		if (loginHint) {
      params.append("logout_hint", loginHint);
    }

    window.location.href =
      `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout?${params.toString()}`;
  }
);

const azureAuthSlice = createSlice({
  name: "azureAuth",
  initialState,
  reducers: {
    resetAzureAuthState: (state) => {
      state.error = null;
      state.isLoading = false;
      state.callbackError = null;
      state.callbackProcessing = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Existing cases for getAzureLoginUrl
      .addCase(getAzureLoginUrl.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAzureLoginUrl.fulfilled, (state, action) => {
        state.isLoading = false;
        state.loginUrl = action.payload.url;
      })
      .addCase(getAzureLoginUrl.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || "Failed to get login URL";
      })

      // New cases for processAuthCallback
      // .addCase(processAuthCallback.pending, (state) => {
      //   state.callbackProcessing = true;
      //   state.callbackError = null;
      // })
      // .addCase(processAuthCallback.fulfilled, (state) => {
      //   state.callbackProcessing = false;
      // })
      // .addCase(processAuthCallback.rejected, (state, action) => {
      //   state.callbackProcessing = false;
      //   state.callbackError = action.payload as string;
      // });
  },
});

export const { resetAzureAuthState } = azureAuthSlice.actions;
export default azureAuthSlice.reducer;
